/**
 * @jsxRuntime classic
 * @jsx jsx
 */
// @ts-ignore -next-line

import React, { useCallback } from 'react';

import { css, cssMap, jsx } from '@compiled/react';
import { useIntl } from 'react-intl-next';

import {
	CustomBlock,
	MediaPlacement,
	PreviewBlock,
	Card as SmartCard,
	SmartLinkSize,
	SmartLinkTheme,
	SnippetBlock,
	TitleBlock,
} from '@atlaskit/smart-card';
import { token } from '@atlaskit/tokens';
import {
	CommonCardWrapperStyles as CommonCardWrapper,
	EmptyAndSmallCardWrapper as EmptyCardWrapper,
	getSmartCardFallbackUrl,
	type ItemDimensions,
	type OnFailed,
	type OnSucceeded,
	useProductDependencies,
} from '@atlassian/company-hub-common';
import {
	CustomImageUploadRenderer,
	getImageTypeToShow,
} from '@atlassian/company-hub-common/media-components';

import { CardSize } from '../../../../types';

import { messages } from './messages';

type EmptyCardProps = {
	cardDimensions: ItemDimensions;
	cardId: string;
	contentId: string;
	description?: string;
	imageAltText?: string;
	imageSrc?: string;
	isInViewMode: boolean;
	onCardFailed: OnFailed;
	onCardSucceeded: OnSucceeded;
	size: CardSize;
	title?: string;
};

const titleBlockStyles = css({
	marginTop: token('space.0'),
	// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'[data-smart-element-link]': {
		fontWeight: token('font.weight.medium'),
		flexShrink: 0,
	},
	// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'a, a:visited, a:focus, a:hover, a:active': {
		textDecoration: 'none',
		pointerEvents: 'none',
	},
});

const titleBlockMarginTopStyles = cssMap({
	small: {
		marginTop: token('space.0'),
	},
	other: {
		marginTop: token('space.100'),
	},
});

const titleBlockLinkTextColorStyles = css({
	// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'[data-smart-element-link]': {
		color: token('color.text'),
	},
});

const titleBlockLinkTextSubtlestColorStyles = css({
	// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'[data-smart-element-link]': {
		color: token('color.text.subtlest'),
	},
});

const previewStyles = css({
	flexGrow: 1,
});

const customImageUploadStyles = cssMap({
	left: {
		position: 'absolute',
		width: '72px',
		top: token('space.0'),
		bottom: token('space.0'),
		left: token('space.0'),
	},
	right: {
		marginLeft: 'calc(var(--container-gap-left) * -1)',
		marginRight: 'calc(var(--container-gap-right) * -1)',
		// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		'&:first-of-type': {
			marginTop: 'calc(var(--container-padding) * -1)',
		},
		// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		'&:last-of-type': {
			marginBottom: 'calc(var(--container-padding) * -1)',
		},
	},
});

export const EmptyCard = ({
	contentId,
	cardId,
	size,
	title,
	description,
	imageSrc,
	cardDimensions,
	isInViewMode,
	onCardSucceeded,
	onCardFailed,
	imageAltText,
}: EmptyCardProps) => {
	const intl = useIntl();
	const { submitError } = useProductDependencies();

	const handleResolve = useCallback(() => {
		onCardSucceeded(cardId);
	}, [onCardSucceeded, cardId]);

	const handleError = useCallback(
		(error: any) => {
			submitError?.(error);
			onCardFailed(error);
		},
		[onCardFailed, submitError],
	);

	const { showUnsplashImage, showCustomUploadedImage, hasImage } = getImageTypeToShow({
		imageSrc,
	});

	const getSmartCardSize = useCallback(() => {
		switch (size) {
			case CardSize.SMALL:
			case CardSize.MEDIUM:
				return SmartLinkSize.Medium;
			case CardSize.LARGE:
			default:
				return SmartLinkSize.Large;
		}
	}, [size]);

	const getCardHeight = useCallback(() => {
		if (cardDimensions.height) {
			return cardDimensions.height;
		}

		switch (size) {
			case CardSize.SMALL:
				return 84;
			case CardSize.MEDIUM:
				return 328;
			case CardSize.LARGE:
				return 424;
		}
	}, [cardDimensions.height, size]);

	const fallbackUrl = getSmartCardFallbackUrl();

	return (
		<CommonCardWrapper isInViewMode={isInViewMode} isEmptyCard>
			<EmptyCardWrapper
				size={size}
				data-testid="empty-state-visual-smart-card"
				cardHeight={getCardHeight()}
				hasImage={hasImage}
			>
				<SmartCard
					id={cardId}
					onError={handleError}
					onResolve={handleResolve}
					appearance="inline"
					ui={{
						theme: SmartLinkTheme.Black,
						clickableContainer: false,
						size: getSmartCardSize(),
					}}
					url={fallbackUrl}
				>
					{showUnsplashImage && (
						<PreviewBlock
							ignoreContainerPadding
							placement={size === CardSize.SMALL ? MediaPlacement.Left : undefined}
							// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
							css={previewStyles}
							overrideUrl={imageSrc}
						/>
					)}
					{showCustomUploadedImage && (
						<CustomBlock
							// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
							css={[
								size === CardSize.SMALL
									? customImageUploadStyles.left
									: customImageUploadStyles.right,
							]}
						>
							<CustomImageUploadRenderer
								contentId={contentId}
								imageSrc={imageSrc}
								imageAltText={imageAltText}
								shouldShowErrorMessage={size !== CardSize.SMALL}
							/>
						</CustomBlock>
					)}
					<TitleBlock
						text={title || intl.formatMessage(messages.emptyStatePlaceholderTitle)}
						// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
						css={[
							titleBlockStyles,
							title ? titleBlockLinkTextColorStyles : titleBlockLinkTextSubtlestColorStyles,
							size === CardSize.SMALL
								? titleBlockMarginTopStyles.small
								: titleBlockMarginTopStyles.other,
						]}
						hideIcon
						anchorTarget="_self"
					/>
					{size !== CardSize.SMALL && <SnippetBlock text={description} />}
				</SmartCard>
			</EmptyCardWrapper>
		</CommonCardWrapper>
	);
};
